import { defineMessages, FormattedMessage, FormattedDate } from 'react-intl';
import styled from 'styled-components';
import { Alert, Box, Grid, H2, Image, Text, GridCell } from '../../components';
import { ProductPrice } from '../../components/ProductPlanCard';

import {
  productBySlugQuery_system,
  productBySlugQuery_productBySlug
} from '../../core-types';

type Props = {
  product: productBySlugQuery_productBySlug;
  system: productBySlugQuery_system | null;
};

const messages = defineMessages({
  purchasableFrom: {
    id: 'modules.BuyProduct.ProductComingSoon.purchasableFrom',
    defaultMessage: 'This product is purchasable from {date}'
  },
  systemOpensAt: {
    id: 'system_opens_at_date',
    defaultMessage: 'The system opens at {date}'
  }
});

const OverflowImage = styled.div`
  @media screen and (max-width: ${({ theme }) => theme.breakpoints[2]}) {
    overflow: hidden;
    height: 200px;
    border-radius: 10px;
    img {
      margin-top: -70px;
    }
  }
`;

const ProductDescription = ({ product }) => {
  if (!product) {
    return null;
  }

  if (product.descriptionText) {
    return (
      <Text as="p" mb="xs" whiteSpace="pre-line">
        {product.descriptionText}
      </Text>
    );
  }

  return null;
};

const ProductComingSoon = ({ product, system }: Props) => {
  if (!product) {
    return null;
  }

  return (
    <Grid justifyContent="center">
      {product.image && product.image.url && (
        <GridCell width={[1, 1, 1, 2 / 5]}>
          <OverflowImage>
            <Image
              boxShadow="heavy"
              borderRadius="lg"
              src={product.image.url}
            />
          </OverflowImage>
        </GridCell>
      )}

      <GridCell
        width={[1, 1, 1, 3 / 5]}
        px={[3, 3, 3, 6]}
        flexDirection="column"
        height="100%"
      >
        <Box flex={1} mb="md">
          <H2
            mb={0}
            typoStyle="md"
            mt={[4, 4, 4, 0]}
            data-testid="Checkout__Heading"
          >
            {product.name}
          </H2>

          <ProductPrice {...product} />

          <ProductDescription product={product} />

          {product.purchasableFrom && (
            <Text as="p" mb="xs">
              <FormattedDate
                year="numeric"
                month="long"
                day="numeric"
                value={product.purchasableFrom}
              >
                {value => (
                  <FormattedMessage
                    {...messages.purchasableFrom}
                    values={{ date: value }}
                  />
                )}
              </FormattedDate>
            </Text>
          )}

          {system &&
            !system.inSeason &&
            system.nextSeason &&
            system.nextSeason.startDate && (
              <Alert variant="info" mb="xs" mt="xs">
                <FormattedMessage
                  {...messages.systemOpensAt}
                  values={{
                    date: (
                      <FormattedDate
                        year="numeric"
                        month="long"
                        day="numeric"
                        value={system.nextSeason.startDate}
                      />
                    )
                  }}
                />
              </Alert>
            )}
        </Box>
      </GridCell>
    </Grid>
  );
};

export default ProductComingSoon;
